import { apiFetch } from './http';

export interface Objective {
  id: string;
  type: string;
  title?: string;
  position: number[];
  radius?: number;
  priority?: 'main' | 'secondary' | 'hidden';
  /** The objective's weight in the ending (engine step E9b): an added one on a scripted night is secondary or additional. */
  role?: 'main' | 'secondary' | 'additional';
  /** The enemy's own move (a reaction to the attack): no task, no mark, nothing a player is shown. */
  silent?: boolean;
  /** Whether the players can see it yet; when absent, `priority: 'hidden'` says the same (the platform's ObjectiveRoles). */
  visibility?: 'hidden' | 'revealed';
  dependsOn?: string[];
  params?: Record<string, unknown>;
  castRefs?: string[];
  /** What a failed objective does to the night: continue, endMission, or reveal:<id>. */
  onFailure?: string;
  /** What this position has to satisfy; the runtime re-checks it on the ground. */
  intent?: PlacementIntent;
}

/** A rectangle, an ellipse or a polygon in terrain metres. */
export interface Shape {
  shape: 'rect' | 'ellipse' | 'polygon';
  center?: number[];
  a?: number;
  b?: number;
  angle?: number;
  points?: number[][];
}

export interface RestrictedZone extends Shape {
  id?: string;
  kind?: 'border' | 'nogo' | 'minefield' | 'nofire' | 'safe';
  label?: string;
  colour?: string;
  /** The game's marker brush and the edge the label stands on, from the kind's look (the platform's MapMarks). */
  brush?: string;
  labelEdge?: number;
}

/** Where the night is played: what the players see drawn, and what bounds the runtime. */
export interface AreaOfOperations extends Shape {
  label?: string;
  colour?: string;
  showToPlayers?: boolean;
  /** The compass bearing of the edge (a rectangle's corner) its label stands on. */
  labelEdge?: number;
  restricted?: RestrictedZone[];
}

/**
 * A mark on the map. `audience: 'staff'` never reaches the game. A `polygon`
 * is a filled area (a village's sector): `points` its corners, `position`
 * where its `text` stands.
 */
export interface MapMarker {
  id: string;
  kind: 'icon' | 'rect' | 'ellipse' | 'polyline' | 'polygon';
  symbol?: string;
  position: number[];
  points?: number[][];
  text?: string;
  colour?: string;
  a?: number;
  b?: number;
  angle?: number;
  alpha?: number;
  size?: number;
  /** For a rect or an ellipse: what it says (objective, intel, drawn…), its brush and its label's edge. */
  meaning?: string;
  brush?: string;
  labelEdge?: number;
  audience?: 'players' | 'staff';
  revealAt?: string;
}

export type ForceRole = 'patrol' | 'garrison' | 'guard' | 'static' | 'vehicle' | 'civilian';

/** An element the editors placed by hand. Never drawn on the players' map. */
export interface Force {
  id: string;
  role: ForceRole;
  label?: string;
  side?: 'west' | 'east' | 'resistance' | 'civilian';
  faction?: string;
  category?: string;
  groupClass?: string;
  /**
   * A group preset saved in the catalogue, chosen instead of a CfgGroups class.
   * The platform flattens it into `composition` when the mission is published,
   * so the runtime never asks anything about presets.
   */
  groupPresetId?: string;
  vehicleClass?: string;
  position: number[];
  radius?: number;
  waypoints?: number[][];
  cycle?: boolean;
  count?: number;
  size?: number;
  behaviour?: 'CARELESS' | 'SAFE' | 'AWARE' | 'COMBAT' | 'STEALTH';
  speed?: 'LIMITED' | 'NORMAL' | 'FULL';
  formation?: string;
  stance?: 'AUTO' | 'UP' | 'MIDDLE' | 'DOWN';
  skill?: number;
  watch?: number[];
  activation?: string;
  objectiveId?: string;
  reinforces?: boolean;
  /** A scripted force's scene, posture and post (ENGINE_DESIGN.md §2.4): the script's, shown and never edited. */
  scene?: string;
  posture?: string;
  post?: string;
  /** A scripted force's size bounds: the fewest its scene needs, the most it holds (release D, D11.10). */
  sizeRange?: [number, number];
  /** An added force that answers a scene's own alarm (`scene:camp`). */
  alertWith?: string;
  /** Held until the script releases it: never counted against the night's budget of active groups. */
  held?: boolean;
}

/** One object of a composition, measured from the composition's own middle. */
export interface CompositionObject {
  className: string;
  offset: number[];
  azimuth?: number;
  pitch?: number;
  simple?: boolean;
}

/**
 * A set of objects placed as one: a forward base at the insertion point, a
 * checkpoint on the road, sandbags round a gun. `presetId` names one saved in
 * the catalogue and `objects` is written in when the mission is published, so
 * the runtime builds it without asking the platform anything.
 */
export interface PlacedComposition {
  id: string;
  presetId?: string;
  label?: string;
  position: number[];
  azimuth?: number;
  /** 'none', 'insertion', 'extraction' or 'objective:<id>'. */
  anchor?: string;
  side?: 'west' | 'east' | 'resistance' | 'civilian';
  audience?: 'players' | 'staff';
  flatten?: boolean;
  clearRadius?: number;
  intent?: PlacementIntent;
  objects?: CompositionObject[];
}

export interface CivilianZone {
  id?: string;
  label?: string;
  position: number[];
  radius: number;
  density?: number;
  panic?: boolean;
  vehicles?: number;
}

export interface IntelNote {
  id: string;
  title: string;
  body?: string;
  position?: number[];
  markerId?: string;
  confidence?: 'confirmed' | 'probable' | 'unconfirmed';
  source?: string;
  revealAt?: string;
  /** Objectives this note opens: the secondary nobody knew about until they read the laptop. */
  unlocks?: string[];
}

export type TwistKind =
  | 'orders'
  | 'reinforcements'
  | 'artillery'
  | 'weather'
  | 'extraction'
  | 'discovery'
  | 'civilian'
  | 'ambush'
  | 'informant';

/** A turn the night takes: a radio exchange, a fragmentary order, and something that changes. */
export interface Twist {
  id: string;
  kind: TwistKind;
  title: string;
  body?: string;
  radio?: string[];
  at?: string;
  /** A scripted twist's window closes at this phase (the script's, shown in words). */
  until?: string;
  /** A scripted twist never fires within this many metres of what it moves. */
  notWithin?: number;
  /** Whoever directs was asked to decide it: they may bring it forward, and it still fires at `at` on its own. */
  byHand?: boolean;
  chance?: number;
  reveals?: string[];
  cancels?: string[];
  forces?: string[];
  weather?: string;
  /** A weather twist's way there: the engine's gradual change, step after step (C17). */
  steps?: {
    afterSeconds: number;
    overSeconds: number;
    state?: string;
    overcast: number;
    fog: number;
    rain?: number;
    windStrength?: number;
  }[];
  position?: number[];
  /**
   * What the twist does, as verbs the runtime runs through its phase engine
   * (a script's own, written by the generator); absent, the kind's fixed
   * effect. Kept as written: the designer never edits them, and changing the
   * kind drops them.
   */
  effects?: Array<Record<string, unknown>>;
}

/**
 * One line of «Com funciona aquesta nit» (MECHANICS.md R18, step D1.1): written
 * by the platform on generation and on every save, never by an editor.
 */
export interface HowItWorksLine {
  /** Stable per dynamic: the script's own word, `gen.*` for a generic line. */
  id: string;
  text: string;
  /** The objective the line is about; a player's copy drops it with that objective while it is hidden. */
  objective?: string;
  /** Written by the engine rather than the recipe's script. */
  generic?: boolean;
}

export interface Briefing {
  /** «Com funciona aquesta nit»: shown before the order, read-only. */
  howItWorks?: HowItWorksLine[];
  /** WARNORD: what little is known, sent before the order itself. */
  context?: string;
  warning?: string;
  situation?: string;
  mission?: string;
  execution?: string;
  support?: string;
  command?: string;
  /** What hangs off the order: rules of engagement, call signs, frequencies. */
  annexes?: string;
}

/**
 * What a position has to satisfy on the ground. The platform works the
 * coordinate out offline; the runtime re-checks it when the mission starts and
 * moves it if the terrain says no. Every field is optional and an absent field
 * is not checked.
 */
export interface PlacementIntent {
  surface?: 'land' | 'water' | 'any';
  relativeTo?: 'area' | 'objective' | 'insertion' | 'none';
  minDistance?: number;
  maxDistance?: number;
  outsideArea?: boolean;
  insideArea?: boolean;
  control?: ('friendly' | 'neutral' | 'contested' | 'enemy')[];
  awayFromSettlements?: number;
  /** Metres clear of the players' own base: an enemy sniper pair keeps off their doorstep. */
  clearOfBase?: number;
  flat?: boolean;
  maxGradient?: number;
  clearRadius?: number;
  nearRoad?: number;
  /** Metres with no road inside: a road near the camp, never through it. */
  roadClear?: number;
  highGround?: boolean;
  overwatchOf?: number[];
  placeKind?: string;
  /** Metres the runtime's ground search may move the point from where it was written; unbounded when absent. */
  maxMove?: number;
}

/** Where the night begins and where it ends. */
export interface MissionPoint {
  position?: number[];
  label?: string;
  means?: 'foot' | 'vehicle' | 'helicopter' | 'boat' | 'deployed';
  intent?: PlacementIntent;
  resolved?: { position?: number[]; movedBy?: number; reason?: string; at?: string };
  /** The way out is the base itself: nothing of its own to move. */
  atBase?: boolean;
}

/**
 * Where the players come from and go back to: a camp, a forward base, an
 * airfield apron or a ship. The insertion's own coordinate until the means of
 * travelling between the two exist, but its own point, because the two ask
 * different questions of the ground.
 */
export interface BasePoint {
  position?: number[];
  label?: string;
  kind?: 'fob' | 'camp' | 'airfield' | 'ship' | 'patrol_base';
  presetId?: string;
  facilities?: {
    respawn?: boolean;
    arsenal?: 'ace' | 'bis' | 'none';
    medical?: 'area' | 'ace' | 'none';
    vehiclePark?: boolean;
    helipad?: boolean;
    briefing?: boolean;
  };
  intent?: PlacementIntent;
  resolved?: { position?: number[]; movedBy?: number; reason?: string; at?: string };
}

export interface Definition {
  schemaVersion: string;
  id: string;
  revision: number;
  seed: number;
  title: string;
  summary?: string;
  /** What the generator could not do as asked (`no_place_of_kind:<kind>`), for whoever made the mission. */
  warnings?: string[];
  /** What the platform moved before play, and at which revision (editors only). */
  preflight?: PreflightBlock;
  terrain: { worldName: string; modSetHash?: string; worldSize?: number };
  /**
   * The list the mission was made with: the Workshop mods and Creator DLCs of
   * the server it was made for, and what that server was called. The server will
   * be edited and may be deleted; this is what a page compares against.
   */
  modSet?: {
    hash: string;
    mods: { workshopId: number; name: string }[];
    dlcs?: string[];
    serverName?: string;
  };
  time?: {
    hour?: number;
    weather?: string;
    /** The day the night is played on: it fixes the season, the light and the moon. */
    date?: string;
    /** The night's own clock. It warns and counts down; nothing ends by itself. */
    limitMinutes?: number;
    decidedBy?: { by: string };
  };
  sides: {
    players: {
      side: string;
      faction: string;
      slots: number;
      startPosition?: number[];
      /** How many slots make one group in the published mission. */
      /** The players' own faction flag, as a texture path in the game's files. */
      flag?: string;
      squadSize?: number;
      /** What one squad is made of, in order. */
      roles?: {
        role: string;
        label?: string;
        unitClass?: string;
        loadoutId?: string;
        count?: number;
      }[];
      /**
       * What the players find waiting at the insertion point, empty. Not a
       * support: a support is called for and arrives, and this is a truck
       * somebody has to drive.
       */
      vehicles?: {
        className: string;
        count?: number;
        label?: string;
        /** The catalogue's own kind of this vehicle, when the catalogue knew it. */
        kind?: 'car' | 'land' | 'apc' | 'tank' | 'heli' | 'plane' | 'ship' | 'static';
      }[];
    };
    enemy: {
      side: string;
      faction: string;
      strength?: string;
      /** The one number the forces inherit; 0.3 is the group's own hand-made enemy. */
      skill?: number;
      /** The engine's nine sub-skills by name, as the runtime applies them. */
      skills?: Record<string, number>;
    };
  };
  /** The insertion and the extraction, each with the intent the runtime re-checks. */
  points?: { base?: BasePoint; insertion?: MissionPoint; extraction?: MissionPoint };
  objectives: Objective[];
  cast?: {
    id: string;
    name: string;
    role: string;
    disposition?: string;
    /** Locked in the designer: its re-roll leaves the name alone. */
    locked?: boolean;
    /** A scripted person's scene role and the mechanic that moves them (ENGINE_DESIGN.md §2.4). */
    scene?: string;
    sceneRole?: string;
    mechanic?: string;
  }[];
  supports?: Record<string, number>;
  /**
   * The class each support is made of, when the mod set owns one. The game's own
   * support menu is virtual and ignores it; Simplex Support Services builds every
   * service out of a real vehicle and has to be told which.
   */
  supportAssets?: Record<string, string>;
  settings?: Record<string, unknown>;
  decisionMatrix?: Record<
    string,
    { by: string; opensAt?: string; closesAt?: string; options?: string[] }
  >;
  campaign?: { code: string; title?: string; chapter: number; threads?: string[] };
  /** Where the night is played (designer, "Àrea"). */
  area?: AreaOfOperations;
  /** What the editors drew on the map (designer, "Marcadors"). */
  markers?: MapMarker[];
  /** Enemy, allied and civilian elements placed by hand (designer, "Enemic"). */
  forces?: Force[];
  /** Sets of objects placed as one (designer, "Composicions"). */
  compositions?: PlacedComposition[];
  civilians?: { enabled?: boolean; zones?: CivilianZone[] };
  /** The points of interest and notes the players get before they go. */
  intel?: IntelNote[];
  /** The operation order, its warning order and its annexes. */
  briefing?: Briefing;
  /** The turns the night may take (designer, "Girs"). */
  twists?: Twist[];
  /**
   * A scripted recipe's own script (ENGINE_DESIGN.md §2): its places, scenes,
   * mechanics, phases, clock and end. Only whoever may already see the order of
   * battle gets it (MissionService.maskHidden strips it whole for the rest).
   */
  script?: MissionScript;
}

/** A trigger or a guard of the script's phase graph: keys the runtime's registry knows (ENGINE_DESIGN.md §2.6). */
export type ScriptWhen = Record<string, unknown>;

/** The script block as the designer reads it, never writes it (the owner's decision 6, 2026-09-27). */
export interface MissionScript {
  recipe?: string;
  version?: number;
  objective?: string;
  places?: {
    id: string;
    kind: string;
    variant?: string;
    position?: number[];
    radius?: number;
    settlement?: string;
    roadMetres?: number;
    means?: string;
    /** An alternative a twist moves to: it follows the place it stands in for, never moved on its own. */
    alternativeOf?: string;
  }[];
  scenes?: {
    id: string;
    kind: string;
    label?: string;
    place?: string;
    builder?: string;
    forces?: string[];
    point?: string;
    /** Where the scene stands and how far it reaches, as the generator wrote it. */
    position?: number[];
    radius?: number;
  }[];
  mechanicInstances?: { id: string; use: string; scene?: string }[];
  phases?: {
    id: string;
    title?: string;
    after?: string[];
    when: ScriptWhen;
    do?: Record<string, unknown>[];
  }[];
  /** The script's own clock (§2.8); absent, the generic clock of `time.limitMinutes` stands. */
  clock?: {
    generic?: boolean;
    marks?: { id: string; at?: string; radio?: string; then?: Record<string, unknown> }[];
  };
  twists?: { admitted?: string[] };
  /** The type's own settings as generation resolved them (release D, D6.1). */
  settings?: Record<string, { value: string; by: 'request' | 'default' }>;
  end?: { report?: string[]; outcomes?: { outcome: string; when?: ScriptWhen }[] };
}

export interface SessionSummary {
  code: string;
  state: 'SCHEDULED' | 'LIVE' | 'ENDED';
  panelServerId: number | null;
  missionRevision: number;
  scheduledAt: string | null;
  startedAt: string | null;
  endedAt: string | null;
  outcome: string | null;
  /** The panel's public report, once the session is linked to a match; absent from older platforms. */
  reportUrl?: string | null;
}

/**
 * A mission as a list shows it. The platform answers `GET /api/missions` with
 * these and not with whole missions: a card needs the player count and how many
 * objectives there are, and the definition of every mission in the library was
 * megabytes of JSON nobody read.
 */
export interface MissionCard {
  /** A smoke night's mission, an inspection tour's or a guided test's: only an admin who asked gets one. */
  test?: boolean;
  code: string;
  title: string;
  ownerId: number | null;
  visibility: 'PRIVATE' | 'UNLISTED' | 'PUBLIC';
  worldName: string;
  worldDisplayName?: string;
  modSetHash: string | null;
  /** The server the mission was made for, and what it was called then. */
  panelServerId?: number | null;
  serverName?: string | null;
  recipe: string | null;
  revision: number;
  publishedFolder: string | null;
  publishedRevision: number | null;
  canEdit: boolean;
  editors: number[];
  sessions: SessionSummary[];
  createdAt: string;
  updatedAt: string;
  campaignId: number | null;
  ownerHandle: string | null;
  derivedFrom: { code: string; title: string; ownerHandle: string | null } | null;
  /** How many people the mission seats; the card's "N jugadors". */
  playerSlots: number;
  objectiveCount: number;
  /** The first line of the situation, for the card; never the whole briefing. */
  summary: string | null;
  /** The type's readable name ("Sabotatge"), from the platform's own recipe catalogue. */
  recipeTitle?: string | null;
  /** The type's Bootstrap icon, without the `bi-` prefix. */
  recipeIcon?: string | null;
  /** Which picture the mission is drawn with: AUTO, TERRAIN or TYPE. */
  cover?: 'AUTO' | 'TERRAIN' | 'TYPE' | null;
  /** The author's Steam picture; absent while the platform has not fetched it. */
  ownerAvatarUrl?: string | null;
}

/**
 * One thing the platform refuses, or warns about, in a definition: the code,
 * the JSON pointer of what is wrong, a Catalan sentence and where it is fixed
 * (`move`, `regenerate`, `edit` or `none`).
 */
export interface DefinitionRefusal {
  code: string;
  path: string;
  reason: string;
  fixBy: string;
}

/** One thing the pre-play check judged (release D, D11.9). */
export interface PreflightRow {
  kind: 'objective' | 'force' | 'waypoint' | 'composition' | 'intel' | 'civilian' | string;
  id: string;
  label: string;
  /** water · edge · landMass · base */
  check: string;
  from: number[];
  to: number[] | null;
  metres: number;
}

/** What the platform would move before play, as a dry run on the terrain's survey; nothing is moved yet. */
export interface PreflightReport {
  surveyed: boolean;
  moved: PreflightRow[];
  left: PreflightRow[];
  unplaceable: PreflightRow[];
}

/**
 * What the platform moved before a session (release D, D11.9): written into
 * the definition with the revision it made. Editors only (it names enemy
 * positions); absent when nothing was ever moved.
 */
export interface PreflightBlock {
  revision: number;
  at: string;
  /** The survey's version it judged with; 0 when there was none. */
  survey: number;
  moved: PreflightRow[];
  left: PreflightRow[];
  unplaceable: PreflightRow[];
}

export interface MissionView {
  code: string;
  title: string;
  ownerId: number | null;
  visibility: 'PRIVATE' | 'UNLISTED' | 'PUBLIC';
  /** The world class: the identifier the definition and the panel carry. */
  worldName: string;
  /** What a page shows for the terrain ("Anizay", not "tem_anizay"); absent on older platforms. */
  worldDisplayName?: string;
  modSetHash: string | null;
  recipe: string | null;
  /** The type's readable name and icon, from the platform's recipe catalogue. */
  recipeTitle?: string | null;
  recipeIcon?: string | null;
  /** Which picture the mission is drawn with: AUTO, TERRAIN or TYPE. */
  cover?: 'AUTO' | 'TERRAIN' | 'TYPE' | null;
  revision: number;
  definition: Definition;
  publishedFolder: string | null;
  publishedRevision: number | null;
  canEdit: boolean;
  /**
   * Whether this account may take the mission away as a file. Not the same as
   * being able to read it: the download carries the unmasked definition, so it
   * is for whoever runs servers and for the mission's own authors.
   */
  canDownload: boolean;
  editors: number[];
  sessions: SessionSummary[];
  createdAt: string;
  updatedAt: string;
  campaignId: number | null;
  ownerHandle: string | null;
  derivedFrom: { code: string; title: string; ownerHandle: string | null } | null;
  /** Living missions copied from this one. */
  forks?: number;
  /** The server the mission was made for, which its page proposes first; absent on older platforms. */
  panelServerId?: number | null;
  /**
   * What the platform's structural check finds in the stored revision
   * (editors only): the schema's problems and the script's broken references.
   * While the check only warns, a save that has them is still stored.
   */
  definitionCheck?: DefinitionRefusal[];
  /**
   * Why the mission may not be edited at all (a smoke night's, an
   * inspection's, a campaign chapter already played): the designer opens
   * read-only with the reason. Null or absent when it may be edited.
   */
  editLock?: DefinitionRefusal | null;
  /** What the pre-play check would move before a session (editors only; a dry run, nothing moved). */
  preflight?: PreflightReport | null;
  /** Which rules of the generator made the stored definition (editors only; absent on older platforms). */
  generation?: GenerationView | null;
  /**
   * What has become of that server since (editors only): gone from the panel,
   * or running another mod set, with what was added and taken away by name.
   * Null when no server was named or the panel does not answer.
   */
  server?: {
    id: number;
    name: string | null;
    exists: boolean;
    changed: boolean;
    added: string[];
    removed: string[];
  } | null;
  /** When the owner deleted it; absent on older platforms and null while it is not deleted. */
  deletedAt?: string | null;
}

export interface PatchOp {
  path: string;
  value?: unknown;
  op?: 'set' | 'remove';
}

export const patchMission = (code: string, base: number, ops: PatchOp[]) =>
  apiFetch<MissionView>(`/api/missions/${code}`, { method: 'PATCH', body: { base, ops } });

/**
 * The mission generated again from its own request with one choice changed
 * (ENGINE_DESIGN.md §2.20): the optional objectives by secondary id, or the
 * twists. Null leaves that list as the request had it. The answer is the next
 * revision, on the same code.
 */
export const regenerateMission = (
  code: string,
  base: number,
  change: {
    secondaryObjectives?: string[] | null;
    twists?: string[] | null;
    /** The type's own settings to change, merged over the request's (release D, D6.3). */
    settings?: Record<string, string> | null;
    /**
     * Places moved on the map (release D, D11.6), by id (`camp`, `point:base`)
     * → [x, y]: each honoured among its kind's candidates, or refused.
     */
    pins?: Record<string, [number, number]> | null;
    /**
     * The type's own parameters to change (release D, D11.14): hour, players,
     * squadSize, insertion, extraction, supports, startFrom, enemyStrength, date;
     * each within what the type declares. Sent only when named.
     */
    params?: Record<string, unknown> | null;
  },
) =>
  apiFetch<MissionView>(`/api/missions/${code}/regenerate`, {
    method: 'POST',
    body: regenerateBody(base, change),
  });

/**
 * The request body; `pins`, `params` and `dryRun` only when there are some, as
 * older platforms never read them and the other regenerate bodies stay unchanged.
 */
function regenerateBody(
  base: number,
  change: Parameters<typeof regenerateMission>[2],
  dryRun = false,
): Record<string, unknown> {
  const body: Record<string, unknown> = {
    base,
    secondaryObjectives: change.secondaryObjectives ?? null,
    twists: change.twists ?? null,
    settings: change.settings ?? null,
  };
  if (change.pins) body.pins = change.pins;
  if (change.params) body.params = change.params;
  if (dryRun) body.dryRun = true;
  return body;
}

/** A new name for one person of the cast, nothing stored (the designer's «Torna a tirar»). */
export const rerollCastName = (code: string, castId: string) =>
  apiFetch<{ name: string }>(
    `/api/missions/${code}/design/cast/${encodeURIComponent(castId)}/reroll`,
    { method: 'POST' },
  );

/**
 * The summary said again in other words, nothing stored. Refused 409 `written_by_hand` when the
 * text is not one the generator wrote.
 */
export const rerollStory = (code: string) =>
  apiFetch<{ summary: string }>(`/api/missions/${code}/design/story/reroll`, { method: 'POST' });

/** One place a regeneration moves or keeps (release D, D11.6). */
export interface PlaceMoveView {
  id: string;
  kind: string | null;
  from: number[] | null;
  to: number[];
  /** The place the editor moved. */
  asked: boolean;
  /** answered · kept · reanswered · rewritten */
  outcome: string;
  /** Why a place the editor did not move moves anyway. */
  why: string | null;
}

/** What a regeneration would do, before it is stored (release D, D11.6). */
export interface RegenerateSummaryView {
  revision: number;
  places: PlaceMoveView[];
  /** Every hand edit since the last generation is replaced by the new one. */
  handEditsReplaced: boolean;
  preflightMoved: number;
  preflightUnplaceable: number;
  warnings: string[];
  /** The hand edits carried over the new generation (release D, D11.7); absent on a platform from before it. */
  edits?: {
    carried: number;
    dropped: { path: string; why: string }[];
    texts: { path: string; names: string }[];
  };
}

/** The same regeneration as a dry run: what it would move, and nothing stored. */
export const previewRegeneration = (
  code: string,
  base: number,
  change: Parameters<typeof regenerateMission>[2],
) =>
  apiFetch<RegenerateSummaryView>(`/api/missions/${code}/regenerate`, {
    method: 'POST',
    body: regenerateBody(base, change, true),
  });

/** One candidate a scripted place or a point may move to (release D, D11.6). */
export interface PlaceCandidateView {
  x: number;
  y: number;
  radius: number;
  variant: string | null;
  label: string | null;
  /** 0 for the kind's best. */
  rank: number;
}

export interface PlaceCandidatesView {
  place: string;
  kind: string;
  current: number[] | null;
  candidates: PlaceCandidateView[];
  truncated: boolean;
}

/** Every candidate of one of a mission's places or points (`camp`, `point:base`, `point:extraction`). */
export const fetchPlaceCandidates = (code: string, id: string) =>
  apiFetch<PlaceCandidatesView>(
    `/api/missions/${code}/places/${encodeURIComponent(id)}/candidates`,
  );
/**
 * Where a mission can be downloaded from: the folder as a ZIP to unpack under
 * `Documents\Arma 3\mpmissions` and open in Eden, or the same folder as a PBO
 * to drop into a server's `mpmissions`.
 *
 * A plain link and not a fetch: the browser's own download is what saves a file
 * with the name the platform gives it, and the session cookie goes with it.
 */
export const missionDownloadUrl = (code: string, format: 'zip' | 'pbo') =>
  `/api/missions/${code}/download?format=${format}`;

/** Sets the picture the mission is shown with; an editor's call, like the visibility. */
export const setMissionCover = (code: string, cover: 'AUTO' | 'TERRAIN' | 'TYPE') =>
  apiFetch<MissionView>(`/api/missions/${code}/cover`, { method: 'POST', body: { cover } });

export const rotateSessionCode = (code: string) =>
  apiFetch<SessionFull>(`/api/sessions/${code}/rotate`, { method: 'POST' });

export interface RecipeView {
  id: string;
  title: string;
  blurb: string;
  /** light · medium · heavy; null on a planned recipe. */
  enemyStrength: string | null;
  supports: Record<string, number>;
  insertionOptions: string[];
  objectiveTypes: string[];
  /** False while the recipe is planned and cannot be generated yet. */
  available: boolean;
  /** Bootstrap icon name without the `bi-` prefix. */
  icon: string;
  minPlayers: number;
  maxPlayers: number;
  /** Typical duration in minutes. */
  minutes: number;
  /** The twist ids this recipe carries, which the quick start offers first. */
  twists?: string[];
  /** The kind of ground this type needs, when it needs one. */
  needs?: string | null;
  /** Why this terrain cannot host it, when it cannot. */
  unavailableReason?: string | null;
  /** The squad this type is played with, first slot first; what step 5 starts from. */
  roles?: string[];
  /** The optional objectives this type may carry, which the type step lets a creator choose. */
  secondaries?: SecondaryView[];
  /** How many places the enemy holds on the main line: what the prior intel can mark. */
  strongpoints?: number;
  /** A15: this type only makes sense at night (Recipe.NIGHT_ONLY); the hour step offers night hours only. */
  nightOnly?: boolean;
  /** Planned: the generator cannot build it yet. Not the same as a type this server or map cannot play. */
  planned?: boolean;
  /**
   * Whether this server, map and the two factions can play it (ENGINE_DESIGN.md §2.18); true while
   * nothing is known. A type that cannot is shown greyed, with `reasons`.
   */
  compatible?: boolean;
  /** Why it cannot be played here, each with where it is fixed. */
  reasons?: RefusalReason[];
  /** What it will play instead where it admits a fallback, said rather than silent. */
  fallbacks?: RefusalReason[];
  /** The parameters a person may set for it and their ranges (MECHANICS.md R17): the rest is hidden. */
  constraints?: RecipeConstraints | null;
  /** Every optional it accepts: twists and side missions, shared and its own. */
  optionals?: OptionalView[];
  /**
   * How far it is from a night the group can rely on (RecipeStatus): `ready` and `testing` may be
   * picked, `coming` is shown and never picked. Absent from a platform older than the field.
   */
  status?: RecipeStatus;
  /**
   * The status in force in the admin page's words: what an admin decided, or what the code declares.
   * `status` says the same; absent from a platform older than the field.
   */
  state?: RecipeState;
  /** For a recipe in tests, how many of its test cards passed since it entered tests; null otherwise. */
  testsPassed?: number | null;
  /** For a recipe in tests, how many passed cards it needs; null otherwise. */
  testsRequired?: number | null;
  /** The clock its script declares, shown and never edited; null or absent is the generic one of `minutes`. */
  clock?: string | null;
  /** The supports offered only from a phase, by key, each with its sentence (ENGINE_DESIGN.md §2.11). */
  gates?: Record<string, string>;
  /** The values the recipe fixes (`hour`, `weather`), each in words with the recipe's reason; shown read-only. */
  fixed?: { param: string; words: string; reason: string }[];
  /** The type's own settings (release D, D6.1): switches and choices, never rolled. Absent on an older platform. */
  settings?: SettingView[];
  /**
   * Each control of the wizard's last step as the type leaves it (RecipeLimits on the platform):
   * free, limited to some values, or fixed. Absent on a platform older than the field.
   */
  limits?: RecipeLimit[];
  /** What a strength and a skill mean to the generator; null on a planned type, absent on an older platform. */
  enemy?: RecipeEnemy | null;
}

/** One control of the last step as a type leaves it. */
export interface RecipeLimit {
  param: 'hour' | 'weather' | 'supports' | 'twists' | 'wayOut';
  state: 'free' | 'limited' | 'fixed';
  /** The values admitted when limited or fixed (weathers, support keys, twist ids); empty for the hour. */
  values: string[];
  /** The range in Catalan words («entre les 01:30 i les 05:00»); null when free. */
  words: string | null;
  /** The type's own sentence; null when it declares the limit and no reason. */
  reason: string | null;
}

/** What the enemy's strength and skill mean to the generator (RecipeLimits.Enemy). */
export interface RecipeEnemy {
  /** The type's own strength: light · medium · heavy. */
  strength: string;
  /** What the generator uses for a party under the type's minimum when nobody chose a strength. */
  strengthUnder: string;
  /** The same, over its maximum. */
  strengthOver: string;
  /** By strength, the most enemy groups the runtime keeps awake at once. */
  activeGroups: Record<string, number>;
  skillMin: number;
  skillMax: number;
  /** The group's own, which the wizard proposes. */
  skillDefault: number;
}

/**
 * One of a type's own settings (release D, D6.1): a switch (an optional twist
 * the owner made a switch, `off` by default, group `twist`) or a choice of
 * several values (group `options`).
 */
export interface SettingView {
  id: string;
  kind: 'switch' | 'choice';
  /** A switch's are `off`, `on`. */
  values: string[];
  default: string;
  title: string;
  blurb: string;
  /** A Catalan name per value, for a choice. */
  valueTitles: Record<string, string>;
  group: string;
}

/** A recipe's maturity, as the platform declares it once (the owner, 2026-09-27). */
export type RecipeStatus = 'ready' | 'testing' | 'coming';

/** A recipe's state as an admin sets it (the owner, 2026-10-12): the same three, `testing` said `in_tests`. */
export type RecipeState = 'ready' | 'in_tests' | 'coming';

/** The one refusal shape (ENGINE_DESIGN.md §2.20): what it is, why, and which step fixes it. */
export interface RefusalReason {
  code: string;
  /** A Catalan sentence, as the platform wrote it. */
  reason: string;
  /** server · type · map · enemy · ours · start · decide · scan · none */
  fixBy: string;
  /** The request parameter a `param_not_admitted` names (release D, D5.1); null otherwise. */
  param?: string | null;
}

/** What a recipe lets a request set (MECHANICS.md R17). Null is "anything", as it always was. */
export interface RecipeConstraints {
  /** How many the type is written for: a recommendation, warned about, never a refusal (2026-09-28). */
  players: { min: number; max: number } | null;
  /** From `from` to `to`, crossing midnight when `from` > `to`; replaced, never refused (R1). */
  hours: { from: number; to: number; reason: string } | null;
  weather: string[] | null;
  /** Ways in, as the definition's means: foot · vehicle · helicopter · boat · parachute · deployed. */
  insertion: string[] | null;
  extraction: string[] | null;
  /** land · sea · point */
  startFrom: string[] | null;
  supports: string[] | null;
  /** The way out the recipe decides (R2), with the reason; null when the request does. */
  wayOut: { atBase: boolean; reason: string } | null;
  /** The parameters replaced rather than refused when outside (the hour always). */
  replaced?: string[];
  /**
   * What the enemy faction must be (release D, D5.5): irregular only, with the
   * sentence shown beside a faction it does not admit. Null or absent: any.
   */
  enemy?: { irregular: boolean; reason: string } | null;
  /**
   * The type's own rules on a support (release D, D5.9): required (written
   * whatever the request says) or limited to a count its own 0-8 entry keeps,
   * never Simplex's. The wizard shows both fixed, with the reason.
   */
  supportRules?: Record<string, SupportRule>;
}

/** One support rule of a type (release D, D5.9). */
export interface SupportRule {
  required: boolean;
  /** How many calls the type's own entry allows; null when it is not limited. */
  limit: number | null;
  reason: string | null;
  /** The type's own entry id (navalFire); null is the key's own name. */
  entry: string | null;
}

/** One optional a recipe accepts (ENGINE_DESIGN.md §2.19). */
export interface OptionalView {
  /** What the request names it by: a twist id, or a secondary id. */
  key: string;
  optional: string;
  kind: 'twist' | 'side';
  scope: 'shared' | 'own';
  title: string;
  blurb: string;
  /** When a twist may fire, in words; null for its own default. */
  window: string | null;
  at: string | null;
  offers: string[];
}

/** One recipe against every map of a server, keyed by the map's class in lower case. */
export interface RecipeFit {
  compatible: boolean;
  reasons: RefusalReason[];
  fallbacks: RefusalReason[];
}

/** What else the recipe list is judged against, beyond the map and the mod set. */
export interface RecipeContext {
  playerFaction?: string | null;
  enemyProfileId?: number | null;
  era?: string | null;
}

/** One optional objective a recipe may carry, as the type step offers it. */
export interface SecondaryView {
  id: string;
  title: string;
  /** 0..1: how often the seed takes it when nobody chooses. */
  chance: number;
  /** Opened by something found on the ground rather than told at the briefing. */
  foundByIntel: boolean;
  /** Whether the enemy holds a place of its own for it, one more the intel can mark. */
  strongpoint?: boolean;
  /** The recipe's own optional rather than a shared side mission. */
  own?: boolean;
}

/** One turn a night can take, as the quick start offers it (`/api/twists`). */
export interface TwistView {
  id: string;
  kind: TwistKind;
  title: string;
  body: string;
  /** How often the seed would fire it when nobody chooses, as a percentage. */
  chancePercent: number;
}

export interface GenerationRequest {
  recipeId: string;
  worldClass: string;
  /** Ignored when a server is named: the mod set is what that server runs. */
  modSetHash: string | null;
  /** When the night is set (modern, coldwar, vietnam, ww2); null is the server's own proposal. */
  era?: string | null;
  playerSide: string;
  playerFaction: string | null;
  /**
   * The faction profile the players are, when two share a class (a hand-made faction
   * plays as the class it was cloned from): what the profile names beyond its class,
   * such as its column trucks, is read from it. Null or absent: the class is the faction.
   */
  playerProfileId?: number | null;
  enemyProfileId: number | null;
  players: number;
  hour: number | null;
  weather: string | null;
  supports: Record<string, number> | null;
  decisions: Record<string, string> | null;
  insertionOptions: string[] | null;
  seed: number | null;
  title: string | null;
  /**
   * The twists the creator chose. Null leaves them to the seed and the
   * recipe; an empty array is a deliberate night with no turns in it.
   */
  twists?: string[] | null;
  /**
   * The type's own settings, by id (release D, D6.1): a switch's `on`, a
   * choice's value. Null or a missing id is the setting's default; never rolled.
   */
  settings?: Record<string, string> | null;
  /** A settlement to build the mission around, or null to let the seed choose. */
  preferredPlace?: string | null;
  /** light · medium · heavy, overriding the recipe's, or null. */
  enemyStrength?: string | null;
  /** 0.1..1, or null to follow the strength. */
  enemySkill?: number | null;
  /** Whether an enemy who sees one of his own dead reacts; null is on. */
  enemiesFindBodies?: boolean | null;
  /**
   * What the players are told about the enemy before they set out. Null leaves
   * the generator's own answer: the zone, two marks and one of them wrong.
   */
  intel?: {
    area: boolean;
    strongpoints: boolean;
    quality: string;
    marks: number;
    falseMarks: number;
  } | null;
  /** How many slots make one squad; null for the definition's eight. */
  squadSize?: number | null;
  /**
   * What one squad is made of, in order. Null or empty leaves the recipe's own
   * table, which is what every mission generated before the quick start grew a
   * step for our own forces used.
   */
  roles?: GenerationRole[] | null;
  /**
   * Who flies the players' helicopter when the faction names its own crew (a
   * hand-made faction's squad, the 1RA's desert Marine pilot): the class and
   * saved loadout of the pilot and the copilot. Null flies it with the
   * faction's own pilot class in its default kit.
   */
  aircrew?: GenerationRole | null;
  /** 'sea' starts the night on a ship off the coast; anything else starts it on land. */
  startFrom?: 'land' | 'sea' | null;
  /** The creator's own point for the base, in terrain metres; null leaves it to the search. */
  startAt?: [number, number] | null;
  /** CfgVehicles classes the players find waiting at the insertion point. */
  playerVehicles?: string[] | null;
  /** The ways out on offer, in the recipes' words; null leaves the way out to the way in's means. */
  extractionOptions?: string[] | null;
  /** The night ends where it began (the default): the base is the extraction, by the way in's own means. */
  extractionAtBase?: boolean | null;
  /** The orders in a folder on the briefing table, to be picked up; null or false leaves them with the briefing. */
  ordersFolder?: boolean | null;
  /** The recipe's optional objectives by id: null lets the seed roll each, empty is none. */
  secondaryObjectives?: string[] | null;
  /**
   * A composition preset built at the insertion point: a base, a FOB, a landing
   * site with its own lights. Null leaves the players standing in a field, which
   * is what every generated mission did.
   */
  startComposition?: string | null;
  /**
   * A composition preset built at every objective the enemy holds: sandbags,
   * wire, a checkpoint's own props.
   */
  enemyDefences?: string | null;
}

/** One line of the players' own order of battle, as the generator takes it. */
export interface GenerationRole {
  role: string;
  label?: string | null;
  unitClass?: string | null;
  loadoutId?: string | null;
  count?: number | null;
}

export const fetchRecipes = () => apiFetch<RecipeView[]>('/api/recipes');
export const fetchTwists = () => apiFetch<TwistView[]>('/api/twists');
/**
 * `panelServerId`: the server the quick start was told, which the mission's page proposes first.
 * `visibility`: absent, the mission is born at the group's level (PUBLIC: every member sees it);
 * 'PRIVATE' keeps it to its author, its editors, the people who play it and admins.
 */
export const generateMission = (
  request: GenerationRequest,
  panelServerId?: number | null,
  visibility?: MissionView['visibility'] | null,
) => {
  const query = [
    panelServerId == null ? null : `panelServerId=${panelServerId}`,
    visibility == null ? null : `visibility=${visibility}`,
  ].filter((q) => q != null);
  return apiFetch<MissionView>(
    query.length === 0 ? '/api/missions/generate' : `/api/missions/generate?${query.join('&')}`,
    { method: 'POST', body: request },
  );
};
/**
 * How a server stands to a mission, class by class (the platform's
 * `MissionCompatibility`): `same` mod set, `compatible` (another mod set with the
 * world and every unit, vehicle and object class), `changes` (the classes in
 * `missingClasses` are not there), `no_terrain`, or `unknown` when nobody has
 * read what the server runs now.
 */
export interface ServerRating {
  serverId: number;
  level: 'same' | 'compatible' | 'changes' | 'no_terrain' | 'unknown';
  missingClasses: string[];
}
/** Every server the panel lists, rated against the mission; empty when the panel does not answer. */
export const fetchMissionServers = (code: string) =>
  apiFetch<ServerRating[]>(`/api/missions/${code}/servers`);
/** Another server for the mission's page to propose; null forgets it. */
export const setMissionServer = (code: string, panelServerId: number | null) =>
  apiFetch<MissionView>(`/api/missions/${code}/server`, {
    method: 'POST',
    body: { panelServerId },
  });
/** `tests` brings the test missions back, for an admin; anybody else never gets one, asked or not. */
export const fetchMissions = (tests = false) =>
  apiFetch<MissionCard[]>(tests ? '/api/missions?tests=1' : '/api/missions');
/** The bin: what this account deleted and may still put back. */
export const fetchDeletedMissions = () => apiFetch<MissionCard[]>('/api/missions/deleted');
/**
 * Deletes a mission. The row stays so the nights it was played keep their
 * reports; `purge` (admins) removes it and them for good. A mission with a live
 * or scheduled session is refused with `not_possible`.
 */
export const deleteMission = (code: string, purge = false) =>
  apiFetch<null>(`/api/missions/${code}${purge ? '?purge=true' : ''}`, { method: 'DELETE' });
export const restoreMission = (code: string) =>
  apiFetch<MissionView>(`/api/missions/${code}/restore`, { method: 'POST' });
export const fetchMission = (code: string) => apiFetch<MissionView>(`/api/missions/${code}`);

/**
 * Whether a stored mission is older than the generator's current rules. A
 * mission keeps the definition it was generated with; rules added since reach
 * it only when it is generated again.
 */
export interface GenerationView {
  /** The generator's revision the mission was made with; null before the number was written. */
  revision: number | null;
  /** The platform's version at that moment; null when it is not known. */
  platform: string | null;
  /** The generator's revision of this platform. */
  current: number;
  /** Rules added since would change what the mission gets. */
  stale: boolean;
  /** This account may generate it again, and the mission keeps the request to do it from. */
  regenerable: boolean;
  /** The mission's own revision, which a regeneration is asked against. */
  missionRevision: number;
}

/** The same without the definition, for a page that only starts the mission (a test card). */
export const fetchMissionGeneration = (code: string) =>
  apiFetch<GenerationView>(`/api/missions/${code}/generation`);
/**
 * The whole definition as the next revision. `base` is the revision the editor
 * holds: the platform refuses a save over one it no longer has with
 * `stale_revision`, as it refuses a stale patch.
 */
export const saveMission = (code: string, definition: Definition, note: string, base: number) =>
  apiFetch<MissionView>(`/api/missions/${code}`, {
    method: 'PUT',
    body: { definition, note, base },
  });
export const setMissionVisibility = (code: string, visibility: MissionView['visibility']) =>
  apiFetch<MissionView>(`/api/missions/${code}/visibility`, {
    method: 'POST',
    body: { visibility },
  });
export const forkMission = (code: string) =>
  apiFetch<MissionView>(`/api/missions/${code}/fork`, { method: 'POST' });
/** One revision of a mission: who (handle, empty when the account is gone), when, and the note the change carried. */
export interface RevisionView {
  revision: number;
  authorId: number;
  authorHandle?: string;
  note: string;
  createdAt: string;
}

export const fetchHistory = (code: string) =>
  apiFetch<RevisionView[]>(`/api/missions/${code}/history`);

/** An older revision back on top, as a new revision; nothing is rewritten. */
export const restoreRevision = (code: string, revision: number) =>
  apiFetch<MissionView>(`/api/missions/${code}/revisions/${revision}/restore`, { method: 'POST' });
/**
 * The terrain survey's height grid, for the map that draws the ground. Absent
 * (404) until somebody has surveyed that terrain, which is the normal state:
 * a survey is one server run and is asked for, never run in a batch.
 */
export const fetchHeightmap = (world: string) =>
  apiFetch<{
    worldName: string;
    worldSize: number;
    gridStep: number;
    cols: number;
    heights: number[];
    water: [number, number][];
  }>(`/api/catalog/terrains/${encodeURIComponent(world)}/heightmap`);

/** One place the generator would choose, on the wizard's maps. */
export interface CandidateView {
  name: string;
  /** site · airfield · settlement · … for an objective; site · airfield · camp · ship for a base. */
  kind: string;
  x: number;
  y: number;
  radius: number;
  /** For a base: inside the band the search would use round the named objective. */
  inReach: boolean;
  /**
   * For a settlement: the houses the survey counted in it, each counted once for the town of the highest rank
   * that reaches it; null for any other kind or with no count, absent from a platform older than the field.
   */
  houses?: number | null;
}

export interface CandidatesView {
  worldClass: string;
  worldSize: number;
  /** The ground the type needs, or null. */
  needs: string | null;
  objectives: CandidateView[];
  /** The objective the creator named, when they did. */
  objective: CandidateView | null;
  bases: CandidateView[];
  band: [number, number];
}

/** Every place one type could take on one terrain: the objectives, and the bases round the named one. */
export const fetchCandidates = (
  recipeId: string,
  worldClass: string,
  modSetHash: string,
  place?: string,
  means?: string,
) => {
  const q = new URLSearchParams({ worldClass, modSetHash });
  if (place) q.set('place', place);
  if (means) q.set('means', means);
  return apiFetch<CandidatesView>(`/api/recipes/${encodeURIComponent(recipeId)}/candidates?${q}`);
};

/** Adds what the recipes are judged against: our faction, the enemy's profile, the era. */
function withContext(q: URLSearchParams, ctx?: RecipeContext): URLSearchParams {
  if (ctx?.playerFaction) q.set('playerFaction', ctx.playerFaction);
  if (ctx?.enemyProfileId != null) q.set('enemyProfileId', String(ctx.enemyProfileId));
  if (ctx?.era) q.set('era', ctx.era);
  return q;
}

/**
 * The recipes, each judged against the server's mod set, the map and the two
 * factions when they are known (ENGINE_DESIGN.md §2.18): `compatible`, the
 * reasons and the fallbacks. With nothing known, every recipe is offered.
 */
export const fetchRecipesFor = (worldClass?: string, modSetHash?: string, ctx?: RecipeContext) => {
  if (!modSetHash) return apiFetch<RecipeView[]>('/api/recipes');
  const q = new URLSearchParams({ modSetHash });
  if (worldClass) q.set('worldClass', worldClass);
  return apiFetch<RecipeView[]>(`/api/recipes?${withContext(q, ctx)}`);
};

/** One recipe on every map of a server's mod set: what the map step greys a terrain with. */
export const fetchRecipeWorlds = (recipeId: string, modSetHash: string, ctx?: RecipeContext) =>
  apiFetch<Record<string, RecipeFit>>(
    `/api/recipes/${encodeURIComponent(recipeId)}/worlds?${withContext(new URLSearchParams({ modSetHash }), ctx)}`,
  );

/**
 * What the PLANOPS Atlas has for a terrain: the tile pyramid the map draws the
 * ground from. `available` false is the ordinary answer for a terrain the
 * Atlas does not carry, and the flat picture stands in.
 */
export interface TerrainMapView {
  available: boolean;
  world: string;
  title: string;
  attribution: string;
  sizeInMeters: number;
  /**
   * The ground the pyramid's square covers, a little more than `sizeInMeters`: the Atlas rounds the
   * zoom-0 tile up to whole pixels (Altis 30 787.9 m for 30 720 m). Absent or 0 on a platform that
   * does not say, and the square is then the terrain's edge as it was drawn before.
   */
  tileExtentInMeters?: number;
  layer: string;
  minZoom: number;
  maxZoom: number;
  tileSize: number;
}

export const fetchTerrainMap = (world: string) =>
  apiFetch<TerrainMapView>(`/api/terrains/${encodeURIComponent(world)}/map`);

/** One tile, through the platform: the Atlas sees one address, not every player's. */
export const terrainTileUrl = (world: string, layer: string, z: number, x: number, y: number) =>
  `/api/terrains/${encodeURIComponent(world)}/tiles/${encodeURIComponent(layer)}/${z}/${x}/${y}`;

/**
 * The terrain's picture. A card wants the small variant; a map that pans and
 * zooms wants the large one, or the ground under it is a blur.
 */
/**
 * The terrain's picture. `tile` is what a grid of thumbnails or a table should
 * ask for (320 px on the long side); `card` fills a column; `map` is the large
 * one the designer draws its map from. Asking for the size the page draws is
 * not a detail: the terrain step of the quick start asks for every terrain of
 * the preset at once.
 */
export const terrainImageUrl = (world: string, size: 'tile' | 'card' | 'map' = 'card') =>
  `/api/terrains/${encodeURIComponent(world)}/image${size === 'card' ? '' : `?size=${size}`}`;

/**
 * A mission's own picture: its terrain's map cut to where the night is played.
 * The revision is part of the address, so a browser keeps the picture for good
 * and asks again only when the mission has changed. 404 while the platform has
 * none, and the caller draws the terrain or the type instead.
 */
export const missionCoverUrl = (code: string, revision: number) =>
  `/api/missions/${encodeURIComponent(code)}/cover.jpg?rev=${revision}`;

/** One person the panel lists on the server: `name` is the game's, `displayName` and `handle` a linked account's. */
export interface LiveRosterRow {
  name: string;
  displayName: string | null;
  handle: string | null;
  side: string | null;
}

export interface SessionFull {
  session: SessionSummary;
  missionCode: string;
  missionTitle: string;
  definition: Definition;
  lastEventSeq: number;
  canDirect: boolean;
  /** The mission's own author or an admin: the only readers offered the removal of the night. */
  canDelete?: boolean;
  /** The revision the mission folder on the server carries; null until published. Compare with `definition.revision`. */
  publishedRevision?: number | null;
  reportUrl: string | null;
  campaign: { code: string; title: string; chapter: number; delta: unknown | null } | null;
  briefingReads: number;
  briefingReadByMe: boolean;
  /** The commander of the night (study 5.13), appointed or voted; null while nobody is. */
  commander: { id: number; handle: string; displayName: string } | null;
  /** Who the panel says is on the server now; signed-in readers of a live session, null or absent when it cannot say. */
  liveRoster?: LiveRosterRow[] | null;
  /**
   * Whether the reader is this session's commander of the night: on this session only, its votes
   * are theirs to open and close as well, and they can hand the command over. False when absent.
   */
  commandsTonight?: boolean;
  /**
   * A start of this session that is under way: the step it is on (`preparing`, `candidate`, `checking`,
   * `stopping`, `template`, `starting`, `waiting`) and since when. While it is, the server being stopped
   * is not a server that is down, and the start is not offered again. Null or absent when none is.
   */
  starting?: { step: string; since: string } | null;
  /** The server the session runs on (plan 3.13); host and connect link only when the platform knows the address.
   * `password` is the players' one, the one a session page may show; the admin password never leaves the panel. */
  server: {
    name: string;
    host: string | null;
    port: number;
    connectUrl: string | null;
    password?: string | null;
  } | null;
  /**
   * The vehicles the players find at their start, each with a name a person
   * reads: the definition's own label, else the catalogue's name, else empty.
   * The class is for the game's own picture of it and is never printed.
   */
  vehicles?: { className: string; displayName: string; count: number; kind: string }[];
  /**
   * The class each support the night has is made of (the medevac and the transport share the
   * helicopter), with its name as a person reads it or empty. For the game's own picture of the
   * support; absent for a kind the mod set named nothing for.
   */
  supports?: { kind: string; className: string; displayName: string }[];
  /** What the page's banner is drawn from: the same things every list draws a mission's picture with. */
  cover?: {
    worldName: string;
    worldDisplayName: string | null;
    recipe: string | null;
    recipeTitle: string | null;
    recipeIcon: string | null;
    cover: 'AUTO' | 'TERRAIN' | 'TYPE' | null;
  } | null;
}

/** One class of a kit, named when the mod set's catalogue knows it. */
export interface KitItem {
  className: string;
  displayName: string;
}

/**
 * What one role carries (UI doc 5.2): the loadout the publisher writes into the
 * slot, resolved the same way. `primary` is the weapon the picture is of;
 * `items` is the rest worth naming, in reading order. A role with no loadout
 * has `loadout` null and starts in the class's default kit.
 */
export interface RoleKit {
  role: string;
  label: string;
  count: number;
  unitClass: string | null;
  loadout: string | null;
  primary: KitItem | null;
  items: KitItem[];
}

export const fetchSessionKit = (code: string) => apiFetch<RoleKit[]>(`/api/sessions/${code}/kit`);

/**
 * Whoever directs appoints the commander by handle; an empty handle clears. The commander of the
 * night uses the same call to hand the command to another player (never to clear it).
 */
export const appointCommander = (code: string, handle: string, view?: SessionView) =>
  apiFetch<SessionFull>(`/api/sessions/${code}/commander${viewQuery(view)}`, {
    method: 'POST',
    body: { handle },
  });

/** One account the field that names the commander suggests: its handle and the name the pages show. */
export interface CommanderCandidate {
  handle: string;
  displayName: string;
}

/** The accounts whose handle or name contains what was typed, for whoever directs or commands (at most eight). */
export const fetchCommanderCandidates = (code: string, typed: string) =>
  apiFetch<CommanderCandidate[]>(
    `/api/sessions/${code}/commander/candidates?q=${encodeURIComponent(typed)}`,
  );

export interface CommandView {
  seq: number;
  id: string;
  kind: string;
  payload: Record<string, unknown>;
  issuedBy: string;
  issuedAt: string;
  ackedAt: string | null;
  deliveredAt: string | null;
  withdrawnAt: string | null;
}

export const markBriefingRead = (code: string) =>
  apiFetch<{ reads: number; readByMe: boolean }>(`/api/sessions/${code}/briefing/read`, {
    method: 'POST',
  });
export const fetchCommands = (code: string) =>
  apiFetch<CommandView[]>(`/api/sessions/${code}/commands`);
export const withdrawCommand = (code: string, id: string) =>
  apiFetch<CommandView>(`/api/sessions/${code}/commands/${id}`, { method: 'DELETE' });

export interface SessionEvent {
  seq: number;
  at: number;
  kind: string;
  payload: { server?: Record<string, unknown>; client?: Record<string, unknown> };
  receivedAt: string;
}

export const createSession = (
  missionCode: string,
  panelServerId: number | null,
  scheduledAt: string | null,
) =>
  apiFetch<SessionFull>('/api/sessions', {
    method: 'POST',
    body: { missionCode, panelServerId, scheduledAt },
  });

/**
 * Which copy of a session the platform answers with. Every reader gets a
 * player's by default, the mission's author and an admin included: the session
 * page is the page that is shared and draws no spoiler for anybody. The
 * direction page asks for the director's (`direction`), which only whoever can
 * direct the night is given; for anybody else the platform answers the
 * player's copy whatever was asked.
 */
export type SessionView = 'direction';
const viewQuery = (view: SessionView | undefined, first = '?') =>
  view ? `${first}view=${view}` : '';

export const fetchSession = (code: string, view?: SessionView) =>
  apiFetch<SessionFull>(`/api/sessions/${code}${viewQuery(view)}`);
export const fetchSessionEvents = (code: string, after: number, view?: SessionView) =>
  apiFetch<SessionEvent[]>(`/api/sessions/${code}/events?after=${after}${viewQuery(view, '&')}`);
/** Starts the session; `panelServerId` is the director's pick when the session was made without a server. */
export const startSession = (code: string, panelServerId?: number) =>
  apiFetch<SessionFull>(`/api/sessions/${code}/start`, {
    method: 'POST',
    ...(panelServerId ? { body: { panelServerId } } : {}),
  });
/** Another server for a session that has not started; null leaves the choice to the start. */
export const changeSessionServer = (code: string, panelServerId: number | null) =>
  apiFetch<SessionFull>(`/api/sessions/${code}/server`, {
    method: 'POST',
    body: { panelServerId },
  });
export const endSession = (code: string, outcome: string, view?: SessionView) =>
  apiFetch<SessionFull>(`/api/sessions/${code}/end${viewQuery(view)}`, {
    method: 'POST',
    body: { outcome },
  });
export const sendCommand = (code: string, kind: string, payload: Record<string, unknown>) =>
  apiFetch<{ seq: number; id: string }>(`/api/sessions/${code}/commands`, {
    method: 'POST',
    body: { kind, payload },
  });

export interface VoteView {
  id: number;
  key: string;
  question: string;
  options: string[];
  state: 'OPEN' | 'CLOSED';
  result: string | null;
  counts: Record<string, number>;
  myBallot: string | null;
  opensAt: string;
  closesAt: string | null;
  /** Closes itself once this many ballots are in; null means by time or by hand. */
  closesAtBallots: number | null;
  /** Ballots cast so far. */
  ballots: number;
}

export const fetchVotes = (code: string) => apiFetch<VoteView[]>(`/api/sessions/${code}/votes`);
export const openVote = (
  code: string,
  vote: {
    key: string;
    question: string;
    options: string[];
    closesAt: string | null;
    closesAtBallots?: number | null;
  },
) => apiFetch<VoteView[]>(`/api/sessions/${code}/votes`, { method: 'POST', body: vote });
export const castBallot = (code: string, voteId: number, option: string) =>
  apiFetch<VoteView[]>(`/api/sessions/${code}/votes/${voteId}/ballots`, {
    method: 'POST',
    body: { option },
  });
export const closeVote = (code: string, voteId: number) =>
  apiFetch<VoteView[]>(`/api/sessions/${code}/votes/${voteId}/close`, { method: 'POST' });
export const rconSay = (code: string, text: string) =>
  apiFetch<{ ok: boolean; answer: string }>(`/api/sessions/${code}/rcon/say`, {
    method: 'POST',
    body: { text },
  });
export const rconSwitch = (code: string) =>
  apiFetch<{ ok: boolean; answer: string }>(`/api/sessions/${code}/rcon/switch`, {
    method: 'POST',
  });

export interface StreamMessage {
  type: string;
  lastSeq: number;
}

/** How long a stream the server closed for good waits before it is opened again. */
export const STREAM_RETRY_MS = 10000;

/**
 * Subscribes to the session's server-sent events. Returns the unsubscribe
 * function. Browsers without EventSource (and the test runner) get null,
 * and the caller falls back to polling.
 *
 * `onError` hears the stream drop and `onOpen` hears it (re)open, so the
 * caller can ask for whatever it missed in between. A dropped connection the
 * browser retries by itself; a stream the server answered with an error status
 * (a 502 from the proxy while the platform restarts for a deploy) the browser
 * closes for good, and it is opened again here after `STREAM_RETRY_MS`.
 */
export function subscribeSession(
  code: string,
  onMessage: (m: StreamMessage) => void,
  onError?: () => void,
  onOpen?: () => void,
): (() => void) | null {
  if (typeof EventSource === 'undefined') return null;
  let source: EventSource | null = null;
  let stopped = false;
  let retry: ReturnType<typeof setTimeout> | undefined;
  const open = () => {
    const current = new EventSource(`/api/sessions/${code}/stream`);
    source = current;
    current.addEventListener('open', () => onOpen?.());
    current.addEventListener('error', () => {
      onError?.();
      if (current.readyState === EventSource.CLOSED && !stopped) {
        clearTimeout(retry);
        retry = setTimeout(open, STREAM_RETRY_MS);
      }
    });
    current.addEventListener('session', (e) => {
      try {
        onMessage(JSON.parse((e as MessageEvent<string>).data) as StreamMessage);
      } catch {
        // a malformed message costs one poll, nothing more
      }
    });
  };
  open();
  return () => {
    stopped = true;
    clearTimeout(retry);
    source?.close();
  };
}

/** One person who can edit a mission; the author comes first and has no way to be removed. */
export interface MissionEditorEntry {
  accountId: number;
  handle: string;
  displayName: string;
  owner: boolean;
  addedBy: string | null;
  addedAt: string | null;
}

/** Who can edit a mission, and whether the asker (its author or an admin) may change that. */
export interface MissionEditorsView {
  editors: MissionEditorEntry[];
  canManage: boolean;
}

export const fetchMissionEditors = (code: string) =>
  apiFetch<MissionEditorsView>(`/api/missions/${code}/editors`);

export const inviteMissionEditor = (code: string, handle: string) =>
  apiFetch<MissionEditorsView>(`/api/missions/${code}/editors`, {
    method: 'POST',
    body: { handle },
  });

export const removeMissionEditor = (code: string, accountId: number) =>
  apiFetch<MissionEditorsView>(`/api/missions/${code}/editors/${accountId}`, { method: 'DELETE' });

/** Members who could edit, for the invitation field; `code` leaves out those already editing that mission. */
export const searchEditorCandidates = (typed: string, code?: string) =>
  apiFetch<{ accountId: number; handle: string; displayName: string }[]>(
    `/api/missions/editor-candidates?q=${encodeURIComponent(typed)}${
      code ? `&code=${encodeURIComponent(code)}` : ''
    }`,
  );
